import type { InfiniteData, QueryClient } from "@tanstack/vue-query";

import ChartBarIcon from "@/icons/heroicons/chart-bar-micro.svg";
import type { AutomationPage } from "@/lib/api/queries/automations/list";
import type { EmailPage } from "@/lib/api/queries/emails/list";
import type { SnippetPage } from "@/lib/api/queries/snippets/list";
import type { SubscriberPage } from "@/lib/api/queries/subscribers/list";
import type { SurveyPage } from "@/lib/api/queries/surveys/list";
import type { TagPage } from "@/lib/api/queries/tags/list";
import { filterInfiniteQueries } from "@/lib/api/queries/utils";
import { type Metric, METRIC_TO_HIDDEN } from "@/screens/Analytics/lib/queries";
import UNTYPED_METRICS from "@/screens/Analytics/lib/specs.json";
import { INTEGRATION_ID_TO_CONFIGURATION } from "@/screens/Settings/integrations";
import type { Permission } from "@/types/account";
import type { Newsletter } from "@/types/newsletter";
import type { SearchResult } from "@/types/search_result";

import { useNewsletter } from "@/store/stores/account";
import { useStore as useUiStatesStore } from "@/store/stores/ui-states";
import { SEARCH_CONTEXT } from "./routes";

function includes(scopes: string[], type: string): boolean {
  return scopes.length === 0 || scopes.includes(type);
}

export function findDataResults({
  q,
  queryClient,
  newsletter,
  scopes = [],
}: {
  q: string;
  queryClient: QueryClient;
  newsletter: Newsletter;
  scopes?: string[];
}): SearchResult[] {
  const results: SearchResult[] = [];

  // Emails
  if (queryClient && includes(scopes, "email")) {
    const queries = queryClient.getQueriesData<InfiniteData<EmailPage>>({
      queryKey: [newsletter.id, "emails", "list"],
    });

    const found = filterInfiniteQueries(queries, (subscriber) => {
      return subscriber.subject.toLowerCase().includes(q);
    });

    for (const email of found) {
      results.push({
        type: "email",
        text: email.subject,
        obj: email,
      });
    }
  }

  // Subscribers
  if (queryClient && includes(scopes, "subscriber")) {
    const queries = queryClient.getQueriesData<InfiniteData<SubscriberPage>>({
      queryKey: [newsletter.id, "subscribers", "list"],
    });

    const found = filterInfiniteQueries(queries, (subscriber) => {
      return subscriber.email_address.toLowerCase().includes(q);
    });

    for (const subscriber of found) {
      results.push({
        type: "subscriber",
        text: subscriber.email_address,
        obj: subscriber,
      });
    }
  }

  // Surveys
  if (queryClient && includes(scopes, "survey")) {
    const queries = queryClient.getQueriesData<InfiniteData<SurveyPage>>({
      queryKey: [newsletter.id, "surveys", "list"],
    });

    const found = filterInfiniteQueries(queries, (survey) => {
      return (
        survey.identifier.toLowerCase().includes(q) ||
        survey.question.toLowerCase().includes(q)
      );
    });

    for (const survey of found) {
      results.push({
        type: "survey",
        text: survey.identifier,
        obj: survey,
      });
    }
  }

  // Tags
  if (queryClient && includes(scopes, "tag")) {
    const queries = queryClient.getQueriesData<InfiniteData<TagPage>>({
      queryKey: [newsletter.id, "tags", "list"],
    });

    const found = filterInfiniteQueries(queries, (tag) => {
      return tag.name.toLowerCase().includes(q);
    });

    for (const tag of found) {
      results.push({
        type: "tag",
        text: tag.name,
        obj: tag,
      });
    }
  }

  // Automations
  if (queryClient && includes(scopes, "automation")) {
    const queries = queryClient.getQueriesData<InfiniteData<AutomationPage>>({
      queryKey: [newsletter.id, "automations", "list"],
    });

    const found = filterInfiniteQueries(queries, (automation) => {
      return automation.name.toLowerCase().includes(q);
    });

    for (const automation of found) {
      results.push({
        type: "automation",
        text: automation.name,
        obj: automation,
      });
    }
  }

  // Snippets
  if (queryClient && includes(scopes, "snippet")) {
    const queries = queryClient.getQueriesData<InfiniteData<SnippetPage>>({
      queryKey: [newsletter.id, "snippets", "list"],
    });

    const found = filterInfiniteQueries(queries, (snippet) => {
      return (
        snippet.identifier.toLowerCase().includes(q) ||
        snippet.name.toLowerCase().includes(q)
      );
    });

    for (const snippet of found) {
      results.push({
        type: "snippet",
        text: snippet.name,
        obj: snippet,
      });
    }
  }

  return results;
}

export function findStaticResults({
  q,
  permissions,
  scopes = [],
}: {
  q: string;
  permissions: Permission["permissions"];
  scopes?: string[];
}): SearchResult[] {
  const results: SearchResult[] = [];
  if (q === "") {
    return results;
  }

  if (includes(scopes, "page")) {
    const uiStatesStore = useUiStatesStore();

    // Search both the global and contextual routes.
    const potentialRoutes = {
      ...SEARCH_CONTEXT,
      ...uiStatesStore.contextualSearchRoutes,
    };
    for (const [route, config] of Object.entries(potentialRoutes)) {
      if (!config) {
        continue;
      }
      let matchedTerm: string | undefined;
      if (!config.title.toLowerCase().includes(q)) {
        const matchesQuery = (match: string) => {
          const normalizedMatch = match.toLowerCase();
          const normalizedQuery = q.toLowerCase();

          return (
            normalizedQuery.includes(normalizedMatch) ||
            normalizedMatch.includes(normalizedQuery) ||
            q.split(/\s+/).some((word) => normalizedMatch.includes(word))
          );
        };

        if (
          !("matches" in config) ||
          !config.matches ||
          !config.matches.some(matchesQuery)
        ) {
          continue;
        }
        matchedTerm = config.matches.find(matchesQuery);
      }

      if (
        "permission" in config &&
        config.permission &&
        (permissions[config.permission] === undefined ||
          permissions[config.permission] === "none")
      ) {
        continue;
      }

      if ("action" in config) {
        results.push({
          type: "special_action",
          text: config.title,
          matchText: matchedTerm,
          action: config.action,
          obj: {
            id: config.title,
            name: route as any,
          },
        });
        continue;
      }

      results.push({
        type: "page",
        text: config.title,
        matchText: matchedTerm,
        feature: "feature" in config ? config.feature : undefined,
        obj: {
          id: config.title,
          name: route as any,
        },
      });
    }
  }

  if (includes(scopes, "page")) {
    const newsletter = useNewsletter().value;

    for (const [id, config] of Object.entries(UNTYPED_METRICS)) {
      if (!config.name.toLowerCase().includes(q)) {
        continue;
      }

      if (newsletter && METRIC_TO_HIDDEN[id as Metric]?.(newsletter)) {
        continue;
      }

      results.push({
        type: "page",
        text: config.name,
        brandIcon: ChartBarIcon,
        obj: {
          id: config.name,
          name: "/analytics/[id]",
          params: { id },
        },
      });
    }

    for (const config of Object.values(INTEGRATION_ID_TO_CONFIGURATION)) {
      const text = `Configure ${config.name} integration`;

      if (!text.toLowerCase().includes(q)) {
        continue;
      }

      results.push({
        type: "page",
        text: text,
        brandIcon: config.brandIcon,
        obj: {
          id: text,
          ...config.to,
        },
      });
    }
  }

  return results;
}
